import 'core-js/stable';
import 'regenerator-runtime/runtime';
import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { render, hydrate } from 'react-dom';
import { ApolloProvider } from 'react-apollo';
import 'allsettled-polyfill';
import createBrowserApolloClient from '@sm/apollo-clients/dist/browser';
import { L10nProvider } from '@sm/intl';
import { getClientEnvironmentDetails } from '@sm/utils';
import { WrenchTheme, GlobalStyles } from '@wds/styles';
import {
  ErrorBoundary,
  HelmetProvider,
  StaticProvider,
  initializeClientErrorHandler,
  GlobalThemeProvider,
} from '@sm/webassets';
import { FiveHundredErrorPage } from './components/Errors';

import { GlobalStyleReset } from './components/GlobalStyleReset';
import getGraphQLFragmentMatcher from './helpers/fragmentMatcher';
import './entry.scss';
import RespWebApp from './App';

const target = document.getElementById('reactApp');

const allClientStaticData = getClientEnvironmentDetails().isBrowser ? window.SM.__LOAD_PAYLOAD_CACHE__ || {} : {};

/**
 * Our servers inject the payload with the `user` details and, rightfully so, leave out the authentication attribute.
 * Hence, the client/webs need to calculate whether the user is authenticated or not by looking at the userId in the webs.
 * Now, we don't want every app/page to do this; hence, we abstract the calculation out.
 * In addition, we do not want the attribute readily available in the `window` object; hence, we
 * calculate it here and directly add it to the context.
 * Yes, the user can still access it (debugging JS/React Dev Tools) but this way, it's not readily exposed.
 *
 * With this in place, the webs can retrieve the authentication status via the context.
 */
allClientStaticData.user = allClientStaticData.user || {};
allClientStaticData.user.isAuthenticated = allClientStaticData.user.id && allClientStaticData.user.id !== '1';

const {
  environment,
  pageRequestId,
  'client-config': { appName, appVersion, graphQLUri },
  gql: { gqlCache = {} } = {},
  'bugsnag-client-config': bugSnagClientConfig,
  pageOptions,
} = allClientStaticData;

// Setup error handler to be available across the web
initializeClientErrorHandler(bugSnagClientConfig);

let localeMessages = null;

const apolloClient = createBrowserApolloClient({
  uri: graphQLUri,
  cacheHydration: gqlCache,
  pageRequestId,
  fragmentMatcherFn: getGraphQLFragmentMatcher,
  linkOptions: {
    credentials: 'include',
    batchInterval: 30,
  },
  metadata: { appName, appVersion },
  availableLoggedOutPaths: ['/lo-graphql/collector', '/lo-graphql/quizResults', '/lo-graphql/enterpriseThankYou'],
});
const slLanguageLocale = pageOptions?.slLanguageLocale || environment.slLanguageLocale;
const languageCode = pageOptions?.languageCode || environment.languageCode;

const rendered = messages => {
  return (
    <GlobalThemeProvider theme={WrenchTheme}>
      <GlobalStyleReset />
      <GlobalStyles />
      <ErrorBoundary FallbackComponent={FiveHundredErrorPage}>
        <HelmetProvider>
          <ApolloProvider client={apolloClient}>
            <BrowserRouter>
              <StaticProvider value={{ ...allClientStaticData }}>
                <L10nProvider localeMessages={messages} localeCode={slLanguageLocale} languageCode={languageCode}>
                  <RespWebApp />
                </L10nProvider>
              </StaticProvider>
            </BrowserRouter>
          </ApolloProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </GlobalThemeProvider>
  );
};

/**
 * For SSR apps we need to render the CSR version with messages. If we don't do that,
 * - SSR would send localized content
 * - CSR wouldn't have the messages; hence, it'd render `null`
 * - CSR would then fetch the messages via `ComponentDidMount`
 * - CSR would eventually show the same content
 *
 * Instead, let's fetch the messages before hand so that the initial CSR paint
 * is the same as the SSR paint.
 *
 * Note - This has no performance impact as L10NProvider was waiting for messages before
 * displaying anything anyways.
 */
const renderAppWithLocaleMessages = () => {
  return new Promise(resolve => {
    //  no need for translations if environ / pageOptions (if exist) eq en-US
    if (
      environment.slLanguageLocale === 'en-US' &&
      (!pageOptions?.slLanguageLocale || pageOptions?.slLanguageLocale === 'en-US')
    ) {
      resolve(localeMessages);
    } else {
      // use slLanguageLocale from pageOptions or environment in this order
      const appMessagesSlLanguageLocale = pageOptions?.slLanguageLocale || environment.slLanguageLocale;
      // using allsettled inlieu of all, all rejects immediate, allsettled will wait for all promise to evaluate
      Promise.allSettled([
        import(/* webpackChunkName: "i18n/[request]" */ `../locales/translated/${appMessagesSlLanguageLocale}`),
        import(
          /* webpackChunkName: "i18n/webassets/[request]" */ `@sm/webassets/dist/locales/translated/${environment.slLanguageLocale}`
        ),
      ]).then(([appMessages, webassetsMessages]) => {
        resolve({
          ...(appMessages.status === 'fulfilled' ? appMessages.value : []),
          ...(webassetsMessages.status === 'fulfilled' ? webassetsMessages.value : []),
        });
      });
    }
  });
};

renderAppWithLocaleMessages()
  .then(messages => {
    localeMessages = messages;
  })
  .finally(() => {
    (target.innerHTML.trim().length ? hydrate : render)(rendered(localeMessages), document.getElementById('reactApp'));
  });
